import React, { useCallback, useMemo, useState } from 'react';
import {
	Card,
	CardBody,
	CardHeader,
	Col,
	Container,
	Row,
} from 'reactstrap';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import UiContent from '../../../Components/Common/UiContent';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getPowerGenerationHourMeterSummaryQuery } from '../../../slices/powerGenerationSource/thunk';
import SummaryOffcanvasContainer from './Modals/SummaryOffcanvasContainer';
import SummarySection from './SummarySection';

dayjs.extend(utc);

const PowerGenerationSourceSummary = () => {
	const breadcrumbLinks = [
		{ to: '#', label: 'Inicio' },
		{ to: '#', label: 'Operaciones' },
		{ to: '#', label: 'Consumos Por Fuente' },
	];

	const [isRight, setIsRight] = useState(false);
	const [filters, setFilters] = useState({ date_range: [] });

	const toggleRightCanvas = useCallback(() => {
		setIsRight(prev => !prev);
	}, []);

	const handleFiltersSubmit = useCallback(newFilters => {
		setFilters(newFilters);
	}, []);

	// Construir baseFilters con register_date en formato ISO para el backend
	const baseFilters = useMemo(() => {
		const dateRange = filters.date_range || [];
		if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
			return {
				register_date: {
					$between: [
						dayjs.utc(dateRange[0]).startOf('day').toISOString(),
						dayjs.utc(dateRange[1]).endOf('day').toISOString(),
					],
				},
			};
		}
		return undefined;
	}, [filters.date_range]);

	function formatNumber(value) {
		const fixedValue = Number(value).toFixed(1);
		const [integerPart, decimalPart] = fixedValue.split('.');
		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
		return `${formattedInteger},${decimalPart}`;
	}

	const columns = useMemo(
		() => [
			{
				header: 'Centro',
				accessorKey: 'establishment_name',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Año',
				accessorKey: 'year',
				accessorFn: row =>
					typeof row.year === 'string' ? row.year : row.year?.toString() ?? '',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Mes',
				accessorKey: 'month',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Tipo Fuente',
				accessorKey: 'source_type_name',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Fuente',
				accessorKey: 'source_name',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Código RFP',
				accessorKey: 'rfp_code',
				width: '1%',
				dataType: 'multi-select',
				Cell: ({ row }) => {
					const value = row.original.rfp_code;
					if (value == null || value === '') return '-';
					return <span>{String(value)}</span>;
				},
			},
			{
				header: 'Tipo Combustible',
				accessorKey: 'fuel_type_name',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Consumo (L)',
				accessorKey: 'total_consumption',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				width: '1%',
				dataType: 'separator',
				summary: true,
				Cell: ({ row }) => {
					const value = row.original.total_consumption;
					if (value == null || value <= 0) return '-';
					return (
						<span className="badge border border-danger text-danger">
							{formatNumber(value)}
						</span>
					);
				},
			},
			{
				header: 'Horas Trabajadas',
				accessorKey: 'total_hours',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				width: '1%',
				dataType: 'separator',
				summary: true,
				Cell: ({ row }) => {
					const value = row.original.total_hours;
					if (value == null || value <= 0) return '-';
					return (
						<span className="badge border border-success text-success">
							{formatNumber(value)}
						</span>
					);
				},
			},
			{
				header: 'Emisiones CO₂ (kg)',
				accessorKey: 'total_emissions',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				width: '1%',
				dataType: 'separator',
				summary: true,
				Cell: ({ row }) => {
					const value = row.original.total_emissions;
					if (value == null || value <= 0) return '-';
					return (
						<span className="badge border border-warning text-warning">
							{formatNumber(value)}
						</span>
					);
				},
			},
		],
		[]
	);

	return (
		<React.Fragment>
			<UiContent />
			<div className="page-content">
				<Container fluid>
					<BreadCrumb
						title="Consumos Por Fuente"
						links={breadcrumbLinks}
					/>

					<Row>
						<Col lg={12}>
							<Card>
								<CardHeader className="border-0 pb-0">
									<SummarySection
										filters={filters}
										rightCanvasBtn={toggleRightCanvas}
									/>

									<SummaryOffcanvasContainer
										isOpen={isRight}
										toggle={toggleRightCanvas}
										filters={filters}
										onSubmit={handleFiltersSubmit}
									/>
								</CardHeader>

								<CardBody>
									<ODataGrid
										key={`grid_consumos_fuente_${JSON.stringify(filters.date_range)}`}
										columns={columns}
										nameFile="consumos_por_fuente"
										exportDateField="register_date"
										baseFilters={baseFilters}
										getDataFunc={getPowerGenerationHourMeterSummaryQuery}
										remoteOperations={{
											pagination: true,
											sorting: true,
											filtering: true,
										}}
									/>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</div>
		</React.Fragment>
	);
};

export default PowerGenerationSourceSummary;
